import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ote3aftmtnfcfh0dpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Object.fromEntries(
            Array.from(formData.entries()).filter(
              ([key]) => key !== 'phone_alt'
            ) as [string, string][]
          )
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full bg-background-50 py-6 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              关于韩漫阅读
            </h1>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    韩漫阅读是专注于韩国漫画（韩漫）的中文在线阅读平台，致力于为华语地区的韩漫爱好者提供免费、高质量的漫画在线观看服务。我们收录了大量热门韩国漫画大全，支持下拉式阅读方式，让您在任何设备上都能获得流畅舒适的阅读体验。
                  </p>
                  <p>
                    作为韩漫爱好者，您可以在我们的韩漫网站免费观看下拉式韩国漫画大全。我们的漫画在线观看平台涵盖了恋爱、奇幻、动作、剧情、悬疑、喜剧等多种类型的韩国漫画，每天更新最新内容，确保您不会错过任何精彩作品。
                  </p>
                  <p>
                    我们深知每位韩漫爱好者的需求——高清画质、快速加载、便捷的下拉式阅读体验。韩漫网站所有内容均经过精心筛选，只为给您呈现最好看的韩国漫画大全。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20reading%20space%20with%20large%20digital%20screen%20displaying%20colorful%20comic%20illustrations%2C%20warm%20ambient%20lighting%2C%20cozy%20atmosphere%2C%20clean%20design%2C%20contemporary%20interior%2C%20soft%20shadows%2C%20editorial%20photography%20style&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="韩漫阅读品牌形象"
                  className="w-full h-full object-cover object-top"
                  title="韩漫阅读 - 韩国漫画在线阅读平台"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-8 text-center">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-book-open-line',
                  title: '海量韩漫资源',
                  desc: '收录上千部热门韩国漫画大全，涵盖恋爱、奇幻、动作、剧情等多种类型，韩漫免费在线阅读。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '下拉式阅读',
                  desc: '专为移动端优化的下拉式漫画在线观看体验，流畅丝滑，随时随地畅享韩漫阅读。',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '每日更新',
                  desc: '韩漫网站每日同步更新最新章节，热门韩国漫画第一时间为您呈现，追更不等待。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '高清画质',
                  desc: '所有漫画提供高清画质，清晰细腻的画面让您的漫画在线观看体验更加出色。',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-lg bg-background-50 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '小雨',
                  role: '韩漫爱好者',
                  content:
                    '这个韩漫网站真的太好用了！下拉式阅读体验超棒，漫画在线观看非常流畅。找了好多韩国漫画都在这里免费看，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '阿杰',
                  role: '漫画达人',
                  content:
                    '作为重度韩漫爱好者，这个平台满足了我对韩国漫画大全的所有需求。更新速度快，画质清晰，韩漫免费阅读太良心了。',
                  rating: 5,
                },
                {
                  name: '小美',
                  role: '学生读者',
                  content:
                    '同学推荐的这个韩漫网站，从此入坑韩漫！分类很全，每天都有新的韩国漫画更新，下拉式阅读方式特别适合在手机上追漫。',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <i
                        key={i}
                        className="ri-star-fill text-accent-500 text-sm"
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">
                      {review.name}
                    </p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-[720px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们联系。我们会在24小时内回复您。
            </p>

            <form
              id="contact-form"
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  您的称呼 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的称呼"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  电子邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的电子邮箱"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  主题
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="general">一般咨询</option>
                  <option value="feedback">意见反馈</option>
                  <option value="cooperation">商务合作</option>
                  <option value="copyright">版权问题</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">
                  最多500字
                </p>
              </div>

              <input
                name="phone_alt"
                type="text"
                className="spam-guard-input"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-primary-50 text-primary-700 text-sm text-center">
                  <i className="ri-check-line mr-1"></i>
                  留言发送成功！我们会尽快回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-accent-50 text-accent-700 text-sm text-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError || '提交失败，请稍后再试'}
                </div>
              )}
            </form>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-[800px] mx-auto space-y-4">
              {[
                {
                  q: '韩漫网站上的漫画都是免费的吗？',
                  a: '是的，韩漫阅读平台上的所有韩国漫画均为免费阅读，您无需支付任何费用即可在线观看下拉式韩国漫画大全。',
                },
                {
                  q: '支持哪些设备进行漫画在线观看？',
                  a: '我们的韩漫网站支持PC、平板和手机等多种设备，下拉式阅读体验在所有设备上都经过优化，让您随时随地畅享韩国漫画。',
                },
                {
                  q: '韩国漫画多久更新一次？',
                  a: '韩漫网站每日更新，热门韩国漫画会根据原作者的更新频率同步更新，确保您不会错过任何精彩章节。',
                },
                {
                  q: '如何找到我想看的韩国漫画？',
                  a: '您可以使用分类浏览功能按类型筛选韩国漫画大全，也可以在搜索框中输入漫画名称直接查找。韩漫免费资源一应俱全。',
                },
                {
                  q: '需要注册账号才能阅读吗？',
                  a: '不需要。韩漫网站无需注册即可直接在线观看所有漫画内容，真正做到韩漫免费无障碍阅读。',
                },
              ].map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-background-100 rounded-lg overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200 transition-colors">
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于韩漫阅读 - 韩漫免费漫画在线观看平台',
              description:
                '韩漫阅读是专注于韩国漫画的中文在线阅读平台，提供免费下拉式韩国漫画大全在线阅读服务。',
              url: 'https://manhwa-reader.example.com/about',
              isPartOf: {
                '@type': 'WebSite',
                name: '韩漫阅读',
                url: 'https://manhwa-reader.example.com',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://manhwa-reader.example.com/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: '关于我们',
                    item: 'https://manhwa-reader.example.com/about',
                  },
                ],
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '韩漫网站上的漫画都是免费的吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '是的，韩漫阅读平台上的所有韩国漫画均为免费阅读，您无需支付任何费用即可在线观看下拉式韩国漫画大全。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '支持哪些设备进行漫画在线观看？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '我们的韩漫网站支持PC、平板和手机等多种设备，下拉式阅读体验在所有设备上都经过优化。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '韩国漫画多久更新一次？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '韩漫网站每日更新，热门韩国漫画会根据原作者的更新频率同步更新。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '如何找到我想看的韩国漫画？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '您可以使用分类浏览功能按类型筛选韩国漫画大全，也可以在搜索框中输入漫画名称直接查找。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '需要注册账号才能阅读吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '不需要。韩漫网站无需注册即可直接在线观看所有漫画内容。',
                  },
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}